import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import Input from "../../../components/fields/input";
import Textarea from "../../../components/fields/textarea";
import { useUpdateProfileMutation } from "../../../store/Reducer/user-profile";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

export default function PersonalInfo({ profileData }) {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        organization: "",
        masjidName: "",
        address: "",
    });

    const [updateProfile, { isLoading: isUpdating }] = useUpdateProfileMutation();

    useEffect(() => {
        if (profileData) {
            setFormData({
                name: profileData.name || "",
                email: profileData.email || "",
                organization: profileData.organization || "",
                masjidName: profileData.masjidName || "",
                address: profileData.address?.line1 || "",
            });
        }
    }, [profileData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name || !formData.email) {
            toast.error("Name and email are required!");
            return;
        }

        if (formData.name === profileData.name) {
            toast.error("No changes made to update!");
            return;
        }

        const payload = {
            name: formData.name
        }

        try {
            const response = await updateProfile(payload);

            if (response.error) {
                const errorMessage =
                    response.error?.data?.message || "An unexpected error occurred.";
                toast.error(errorMessage);
                return;
            }

            if (response?.data?.message) {
                toast.success(response?.data?.message);
            } else {
                toast.success("Profile updated successfully!");
            }

            navigate("/user-dashboard")
        } catch (error) {
            const errorMessage =
                error?.response?.data?.message || error.message || "An error occurred.";
            console.error("Unexpected Error:", error);
            toast.error(errorMessage);
        }
    };

    return (
        <>
            <div className="note mb-3 py-3 px-3 text-sm bg-blue-100 text-blue-900 rounded-md">
                <span className="font-bold">{t("universalNote")}: </span>You can only change your name here. To change other details, ask us in{" "}
                <Link className="font-bold underline" to="/user-dashboard/support">
                    Help &amp; support
                </Link>.
            </div>

            <div className="bg-white shadow-xl rounded-2xl overflow-hidden">
                <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-6">
                    <div className="grid gap-6 sm:grid-cols-2">
                        <div className="space-y-2">
                            <Input
                                label="Name"
                                type="text"
                                name="name"
                                className="w-full"
                                placeholder="Enter your name"
                                value={formData.name}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="space-y-2">
                            <Input
                                label="Email"
                                type="email"
                                name="email"
                                className="w-full"
                                placeholder="Enter your email"
                                value={formData.email}
                                onChange={handleChange}
                                readOnly
                                disabled
                            />
                        </div>
                    </div>

                    <div className="grid gap-6 sm:grid-cols-2">
                        <div className="space-y-2">
                            <Input
                                label="Organization"
                                type="text"
                                name="organization"
                                className="w-full"
                                placeholder="Enter your organization"
                                value={formData.organization}
                                onChange={handleChange}
                                readOnly
                                disabled
                            />
                        </div>
                        <div className="space-y-2">
                            <Input
                                label="Masjid Name"
                                type="text"
                                name="masjidName"
                                className="w-full"
                                placeholder="Enter your masjid name"
                                value={formData.masjidName}
                                onChange={handleChange}
                                readOnly
                                disabled
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Textarea
                            id="address"
                            label="Address"
                            name="address"
                            placeholder="Enter your address"
                            value={formData.address}
                            onChange={handleChange}
                            readOnly
                            disabled
                        />
                    </div>

                    <div className="flex justify-end space-x-4 pt-6 border-t">
                        <button
                            type="button"
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            onClick={() => navigate("/user-dashboard")}
                        >
                            {t("universalCancel")}
                        </button>
                        <button
                            type="submit"
                            disabled={isUpdating}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isUpdating
                                ? t("user_profile_btn_text_saving")
                                : t("user_profile_btn_text")}
                        </button>
                    </div>
                </form>
            </div>
        </>
    );
}
